import { IconChart, IconUsers, IconLink } from "./icons";

type StatFormat = "int" | "percent";

const LINE_PATH =
  "M0,70 L20,60 L40,65 L60,50 L80,55 L100,40 L120,45 L140,30 L160,35 L180,25 L200,22 L220,28 L240,18 L260,22 L280,12 L300,18 L320,10";
const AREA_PATH = `${LINE_PATH} L320,90 L0,90 Z`;

// A copper-themed mock of the GrowthRail dashboard overview. Not a real
// screenshot - we render the layout in our brand palette so the preview
// matches the marketing site.
export default function DashboardPreview() {
  return (
    <div className="relative isolate transform-gpu">
      {/* glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-x-8 -top-12 -bottom-12 -z-10 bg-[radial-gradient(closest-side,rgba(184,115,51,0.25),transparent_100%)] transform-gpu will-change-[transform,opacity]"
      />

      <div className="overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black shadow-2xl transform-gpu">
        {/* chrome */}
        <div className="flex items-center justify-between gap-3 border-b border-white/5 bg-black/40 px-4 py-2.5">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
            <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
            <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
          </div>
          <div className="hidden flex-1 items-center justify-center sm:flex">
            <div className="flex items-center gap-2 rounded-md border border-white/5 bg-white/[0.03] px-3 py-1 font-mono text-[11px] text-zinc-500">
              <svg className="h-3 w-3 text-copper-400" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2a4 4 0 0 0-4 4v2H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1h-1V6a4 4 0 0 0-4-4Zm-2 6V6a2 2 0 1 1 4 0v2H8Z" /></svg>
              app.growthrail.dev / overview
            </div>
          </div>
          <div className="w-12" />
        </div>

        <div className="grid gap-0">
          {/* main */}
          <div className="p-4 sm:p-5">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="font-display text-sm font-semibold text-white">Overview</h3>
                <p className="text-[11px] text-zinc-500">Acme Referrals · last 30 days</p>
              </div>
              <div className="hidden items-center gap-1.5 rounded-md border border-emerald-500/20 bg-emerald-500/10 px-2 py-1 text-[10px] font-medium text-emerald-300 sm:inline-flex">
                <span className="dash-live-dot relative h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Live
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              <Stat icon={<IconUsers className="h-3.5 w-3.5" />} label="Referrers" target={1284} format="int" delta="+18%" />
              <Stat icon={<IconLink className="h-3.5 w-3.5" />} label="Conversions" target={312} format="int" delta="+24%" />
              <Stat icon={<IconChart className="h-3.5 w-3.5" />} label="Conv. rate" target={24.3} format="percent" delta="+3.1%" />
            </div>

            {/* chart */}
            <div className="mt-4 rounded-xl border border-white/5 bg-white/[0.02] p-3 transform-gpu">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-[11px] font-medium text-zinc-300">Referral conversions</p>
                <div className="flex gap-1 text-[10px] text-zinc-500">
                  <span className="rounded bg-white/5 px-1.5 py-0.5">7d</span>
                  <span className="rounded bg-copper-600/20 px-1.5 py-0.5 text-copper-200">30d</span>
                  <span className="rounded bg-white/5 px-1.5 py-0.5">90d</span>
                </div>
              </div>
              <svg viewBox="0 0 320 90" className="w-full transform-gpu" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="dp-grad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#b87333" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#b87333" stopOpacity="0" />
                  </linearGradient>
                  <clipPath id="dp-clip" clipPathUnits="userSpaceOnUse">
                    <rect className="dash-chart-clip" x="0" y="0" width="0" height="90" />
                  </clipPath>
                </defs>
                <g clipPath="url(#dp-clip)">
                  <path d={AREA_PATH} fill="url(#dp-grad)" />
                </g>
                <path
                  className="dash-chart-line"
                  d={LINE_PATH}
                  fill="none"
                  stroke="#d0814a"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  pathLength={1}
                  strokeDasharray="1"
                  strokeDashoffset="1"
                />
              </svg>
            </div>


          </div>
        </div>
      </div>
    </div>
  );
}



function Stat({
  icon,
  label,
  target,
  format,
  delta,
}: {
  icon: React.ReactNode;
  label: string;
  target: number;
  format: StatFormat;
  delta: string;
}) {
  const initial = format === "percent" ? "0.0%" : "0";
  return (
    <div className="dash-stat-card rounded-xl border border-white/5 bg-white/[0.02] p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500">
        <span className="text-copper-400">{icon}</span>
        {label}
      </div>
      <div
        className="dash-stat-value mt-1 font-display text-lg font-semibold text-white sm:text-xl tabular-nums"
        data-count-target={target}
        data-count-format={format}
      >
        {initial}
      </div>
      <div className="text-[10px] font-medium text-emerald-400">{delta}</div>
    </div>
  );
}


